import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { BadgeCheck, ShieldAlert, ShieldCheck } from 'lucide-react';
import PublicPageShell from '../../components/Public/PublicPageShell';
import { licenseService } from '../../services/api';
import { normalizeVerificationCode, validationSituation } from '../../utils/authorization';

function formatDate(value, withTime = false) {
  if (!value) return '—';
  const date = new Date(withTime ? value : `${value}T12:00:00`);
  return withTime
    ? date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : date.toLocaleDateString('pt-BR');
}

/** Destino do QR impresso na autorização: confere autenticidade sem login e sem dado pessoal do interessado. */
const ValidarDocumento = () => {
  const { codigo: codeParam = '' } = useParams();
  const navigate = useNavigate();
  const [typed, setTyped] = useState(codeParam);
  const code = normalizeVerificationCode(codeParam);

  const { data, error, isLoading } = useQuery(['validar-documento', code], () => licenseService.validateAuthorization(code), {
    enabled: code.length >= 6,
    retry: false,
  });
  const situation = validationSituation(data);
  const notFound = error?.response?.status === 404;

  return (
    <PublicPageShell
      eyebrow="Autenticidade de documento"
      title="Validar autorização"
      lead="Informe o código verificador impresso no documento, ou leia o QR code. A consulta confirma se o documento foi emitido por este órgão e se continua válido."
    >
      <form
        className="flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          const next = normalizeVerificationCode(typed);
          if (next) navigate(`/validar/${next}`);
        }}
      >
        <label htmlFor="codigo-verificador" className="sr-only">
          Código verificador
        </label>
        <input
          id="codigo-verificador"
          className="form-input font-mono uppercase"
          placeholder="Código verificador"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <button type="submit" className="btn btn-primary">
          Validar
        </button>
      </form>

      {isLoading ? <p className="mt-6 text-sm text-slate-500">Consultando…</p> : null}

      {notFound ? (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <ShieldAlert className="h-5 w-5 shrink-0" aria-hidden />
          <p>
            Nenhum documento emitido por este órgão tem o código <span className="font-mono">{code}</span>. Confira o
            código ou procure o órgão emissor.
          </p>
        </div>
      ) : null}

      {data ? (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" aria-live="polite">
          <div className="flex flex-wrap items-center gap-2">
            {situation?.tone === 'success' ? (
              <ShieldCheck className="h-6 w-6 text-emerald-600" aria-hidden />
            ) : (
              <ShieldAlert className="h-6 w-6 text-amber-600" aria-hidden />
            )}
            <h2 className="text-lg font-semibold text-slate-900">{situation?.label}</h2>
          </div>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-slate-500">Documento</dt>
              <dd className="text-slate-900">{data.titulo}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-500">Número</dt>
              <dd className="font-mono text-slate-900">{data.numero}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-500">Emitido em</dt>
              <dd className="text-slate-900">{formatDate(data.emitida_em, true)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-500">Válido até</dt>
              <dd className="text-slate-900">{formatDate(data.validade)}</dd>
            </div>
          </dl>
          <h3 className="mt-5 text-xs uppercase text-slate-500">Assinado eletronicamente por</h3>
          <ul className="mt-2 space-y-1 text-sm text-slate-800">
            {(data.assinantes || []).map((s) => (
              <li key={`${s.nome}-${s.assinado_em}`} className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-emerald-600" aria-hidden />
                {s.nome}, {s.cargo}, em {formatDate(s.assinado_em, true)}
              </li>
            ))}
          </ul>
          <p className="mt-5 break-all text-xs text-slate-500">
            Impressão digital do PDF original (SHA-256): <span className="font-mono">{data.pdf_sha256}</span>
          </p>
        </section>
      ) : null}
    </PublicPageShell>
  );
};

export default ValidarDocumento;
